/**
 * Sysadmin Argus Models — Wave 3 / M6c.
 *
 * TanStack Query hooks for the M6c admin endpoints under
 * /api/v1/admin/platform/. Every hook here is sysadmin-only; the
 * apiClient flows the platform-admin JWT (audience `argusai-admin`)
 * automatically. Tenant-context calls return 401/403 from the API.
 *
 * Wire types match the Zod response schemas in
 * packages/api/src/routes/v1/admin/argus-models.ts. Keeping these
 * shapes in sync is enforced by the OpenAPI doc generator (Wave 1b
 * shipped that for /v1; M6c piggybacks on the same pipeline).
 *
 * Per I-M6.7 these admin types include engine internals + wrapper
 * source — they're sysadmin-only views. The tenant-side
 * /argus-models/available endpoint has its OWN hook + types
 * (separate file when M6c builds the tenant model picker UI; today
 * tenant view is stub).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';

// ─── Brand catalog wire types ────────────────────────────────────────────────

export interface ArgusModelSummary {
  id: string;
  brandId: string;
  displayName: string;
  description: string;
  modality: string;
  factoryDefault: boolean;
  author: string;
  sortOrder: number;
  isActive: boolean;
  /**
   * bR6MODELS02 (R5 P1-2): 'tenant' (user-selectable in chat picker) or
   * 'infra' (RAG / complexity-classifier / image-gen, never user-facing).
   * UI renders an "Infrastructure" status pill for purpose='infra'.
   */
  purpose: 'tenant' | 'infra';
  createdAt: string;
  updatedAt: string;
  retiredAt: string | null;
  engineCount: number;
  assignmentCount: number;
  enabledAssignmentCount: number;
}

export interface ArgusModelEngine {
  id: string;
  argusModelId: string;
  baseLlmId: string;
  baseLlmModelKey: string;
  baseLlmDisplayName: string;
  baseLlmProvenance: string;
  roleLabel: string;
  isDefault: boolean;
  provenanceEligibility: string[];
  defaultTemperature: string;
  defaultTopP: string;
  defaultTopK: number | null;
  defaultMaxTokens: number | null;
  defaultRepeatPenalty: string | null;
  defaultCtxSize: number;
  systemPrompt: string;
  guardrailProfile: string;
  apiBase: string;
  backendModelName: string;
  isActive: boolean;
}

export interface ArgusModelChainEntry {
  id: string;
  wrapperFunctionId: string;
  wrapperFunctionKey: string;
  wrapperFunctionDisplayName: string;
  chainKind: 'inlet' | 'outlet';
  position: number;
  isMandatory: boolean;
  failurePolicy: 'terminal' | 'warn';
  isActive: boolean;
}

export interface ArgusModelDetail extends ArgusModelSummary {
  engines: ArgusModelEngine[];
  chains: ArgusModelChainEntry[];
}

// ─── Base LLM registry ─────────────────────────────────────────────────────

export interface BaseLlm {
  id: string;
  modelKey: string;
  displayName: string;
  family: string;
  sizeParamsB: string | null;
  quantization: string | null;
  contextWindow: number;
  vramGb: string | null;
  modality: string;
  provenance: string;
  provenanceOrigin: string;
  license: string;
  supportedProviders: string[];
  supportsTools: boolean;
  supportsLora: boolean;
  supportsVision: boolean;
  embedDim: number | null;
  sourceUrl: string | null;
  isBuiltin: boolean;
}

// ─── Wrapper function ──────────────────────────────────────────────────────

export type WrapperFunctionStatus = 'draft' | 'published' | 'deprecated' | 'retired';

export interface WrapperFunction {
  id: string;
  functionKey: string;
  displayName: string;
  description: string;
  runtime: string;
  source: string;
  /**
   * Wave 3 PR-B — Ed25519 signature over the canonical UTF-8 bytes of
   * `source`. Null when unsigned (the row was just created or the
   * source was edited and not yet re-signed). `is_active=true` requires
   * a non-null signature for non-native runtimes — the API enforces.
   */
  signature?: string | null;
  /** Key id the verifier uses to look up the public key. */
  signingKeyId?: string | null;
  category: string;
  appliesTo: string;
  latencyBudgetMs: number;
  version: string;
  isActive: boolean;
  /** PR-G publish lifecycle. */
  status: WrapperFunctionStatus;
  publishedAt: string | null;
  publishedBy: string | null;
  unpublishedAt: string | null;
  unpublishedBy: string | null;
  unpublishReason: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Trust toggle ──────────────────────────────────────────────────────────

export interface TrustToggle {
  id: string;
  configKey: string;
  label: string;
  description: string;
  defaultState: boolean;
  wrapperFunctionId: string;
  wrapperFunctionKey: string;
  appliesToBrands: string[] | null;
  sortOrder: number;
  isActive: boolean;
}

// ─── Assignment matrix ─────────────────────────────────────────────────────

export interface AssignmentRow {
  id: string;
  organizationId: string;
  organizationName: string;
  argusModelId: string;
  brandId: string;
  argusModelEngineId: string;
  engineRoleLabel: string;
  engineBaseLlmKey: string;
  engineProvenance: string;
  isEnabled: boolean;
  assignedAt: string;
  lastModifiedAt: string;
  sysadminNotes: string | null;
}

// ─── Model-change history wire types ─────────────────────────────────────────

/**
 * One `argus_model.*` audit event — the "Model Changes" feed in the
 * Sysadmin Models rail. Mirrors ModelChangeRowSchema in
 * packages/api/src/routes/v1/admin/argus-models.ts (GET /history).
 */
export interface ModelChangeRow {
  id: string;
  /** e.g. argus_model.created, argus_model.engine_load_started. */
  action: string;
  actorEmail: string | null;
  outcome: string | null;
  /** argus_model | argus_model_engine. */
  resourceType: string;
  resourceId: string | null;
  /** Redacted eventData (brandId, displayName, engine counts, …). */
  summary: Record<string, unknown> | null;
  createdAt: string;
}

export interface ModelHistoryPage {
  items: ModelChangeRow[];
  nextCursor: string | null;
}

// ─── Query hooks ───────────────────────────────────────────────────────────

const KEYS = {
  catalog: ['admin', 'argus-models'] as const,
  detail: (id: string) => ['admin', 'argus-models', id] as const,
  history: (cursor: string | undefined) => ['admin', 'argus-models', 'history', cursor ?? 'head'] as const,
  baseLlms: ['admin', 'base-llm-registry'] as const,
  wrapperFunctions: ['admin', 'argus-wrapper-functions'] as const,
  trustToggles: ['admin', 'argus-trust-toggles'] as const,
  assignments: (q: AssignmentQuery) => ['admin', 'tenant-argus-model-assignments', q] as const,
};

export interface AssignmentQuery {
  organizationId?: string;
  brandId?: string;
  limit?: number;
  offset?: number;
}

/**
 * List the brand catalog. Includes per-brand engine + assignment
 * counts so the catalog browser table can render summary stats
 * without N+1 fetches.
 */
export function useArgusModels() {
  return useQuery<{ items: ArgusModelSummary[] }, Error>({
    queryKey: KEYS.catalog,
    queryFn: () => apiClient.get('/admin/platform/argus-models'),
  });
}

/**
 * Full detail for a single brand including its engine pool +
 * function chain composition + tenant assignment summary.
 */
// A model id is always a UUID. Guarding on the shape keeps the detail hook
// from ever firing `/admin/platform/argus-models/<non-uuid>` (which would 422
// against the GET /:id route). The "Model Changes" page at
// /sysadmin/models/history has its own route + hook (useArgusModelHistory)
// and its own backend GET /history endpoint — it is NOT served by this hook.
const MODEL_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function useArgusModelDetail(id: string | null | undefined) {
  return useQuery<ArgusModelDetail, Error>({
    queryKey: id ? KEYS.detail(id) : ['admin', 'argus-models', '__none__'],
    queryFn: () => apiClient.get(`/admin/platform/argus-models/${id}`),
    enabled: !!id && MODEL_ID_RE.test(id),
  });
}

/**
 * Model-change history — the chronological `argus_model.*` audit feed
 * powering the Sysadmin Models → "Model Changes" page. Keyset paginated:
 * pass the prior page's `nextCursor` (an ISO timestamp) to fetch older
 * rows. Each cursor is its own cache entry so "Load more" accumulates in
 * the page component rather than refetching the whole feed.
 */
export function useArgusModelHistory(cursor?: string) {
  return useQuery<ModelHistoryPage, Error>({
    queryKey: KEYS.history(cursor),
    queryFn: () => {
      const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
      return apiClient.get(`/admin/platform/argus-models/history${qs}`);
    },
  });
}

/** Base LLM registry — engine pool source for the create-model wizard. */
export function useBaseLlmRegistry() {
  return useQuery<{ items: BaseLlm[] }, Error>({
    queryKey: KEYS.baseLlms,
    queryFn: () => apiClient.get('/admin/platform/base-llm-registry'),
  });
}

/** Wrapper function library — for criterion 3. */
export function useWrapperFunctions(opts: { status?: WrapperFunctionStatus } = {}) {
  const qs = opts.status ? `?status=${encodeURIComponent(opts.status)}` : '';
  return useQuery<{ items: WrapperFunction[] }, Error>({
    queryKey: opts.status
      ? [...KEYS.wrapperFunctions, { status: opts.status }] as const
      : KEYS.wrapperFunctions,
    queryFn: () => apiClient.get(`/admin/platform/argus-wrapper-functions${qs}`),
  });
}

/** Trust toggles list — for criterion 5. */
export function useTrustToggles() {
  return useQuery<{ items: TrustToggle[] }, Error>({
    queryKey: KEYS.trustToggles,
    queryFn: () => apiClient.get('/admin/platform/argus-trust-toggles'),
  });
}

/**
 * Tenant assignment matrix. Optional filters narrow to a single org
 * or brand; defaults page through 200 rows at a time.
 */
export function useAssignments(query: AssignmentQuery = {}) {
  const params = new URLSearchParams();
  if (query.organizationId) params.set('organizationId', query.organizationId);
  if (query.brandId) params.set('brandId', query.brandId);
  if (query.limit) params.set('limit', String(query.limit));
  if (query.offset) params.set('offset', String(query.offset));
  const qs = params.toString();

  return useQuery<{ items: AssignmentRow[]; total: number }, Error>({
    queryKey: KEYS.assignments(query),
    queryFn: () =>
      apiClient.get(`/admin/platform/tenant-argus-model-assignments${qs ? `?${qs}` : ''}`),
  });
}

// ─── Mutation hooks ────────────────────────────────────────────────────────

export interface CreateEngineInput {
  baseLlmId: string;
  roleLabel?: string;
  isDefault?: boolean;
  provenanceEligibility: Array<'us-only' | 'us-eu' | 'unrestricted'>;
  defaultTemperature?: number;
  defaultTopP?: number;
  defaultTopK?: number;
  defaultMaxTokens?: number;
  defaultRepeatPenalty?: number;
  defaultCtxSize?: number;
  systemPrompt?: string;
  guardrailProfile?: string;
  apiBase?: string;
  backendModelName: string;
}

export interface CreateChainEntryInput {
  wrapperFunctionId: string;
  chainKind: 'inlet' | 'outlet';
  position?: number;
  isMandatory?: boolean;
  failurePolicy?: 'terminal' | 'warn';
}

export interface CreateArgusModelInput {
  brandId: string;
  displayName: string;
  description: string;
  modality: string;
  factoryDefault?: boolean;
  sortOrder?: number;
  // M6c Phase C — nested engine + chain creation. The wizard sends
  // both so the brand is dispatchable on first create.
  engines?: CreateEngineInput[];
  chains?: CreateChainEntryInput[];
}

export function useCreateArgusModel() {
  const qc = useQueryClient();
  return useMutation<{ id: string }, Error, CreateArgusModelInput>({
    mutationFn: (input) => apiClient.post('/admin/platform/argus-models', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.catalog });
    },
  });
}

export interface UpdateArgusModelInput {
  id: string;
  displayName?: string;
  description?: string;
  modality?: string;
  factoryDefault?: boolean;
  sortOrder?: number;
  isActive?: boolean;
}

export function useUpdateArgusModel() {
  const qc = useQueryClient();
  return useMutation<{ id: string }, Error, UpdateArgusModelInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient.patch(`/admin/platform/argus-models/${id}`, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.catalog });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
}

// ─── Wrapper-chain composition (config-as-data) ──────────────────────────
// Add / edit / remove a function in a brand's inlet|outlet wrapper chain.
// Backed by /admin/platform/argus-models/:id/chains[/:chainId].

export interface ChainEntry {
  id: string;
  wrapperFunctionId: string;
  wrapperFunctionKey: string;
  wrapperFunctionDisplayName: string;
  chainKind: 'inlet' | 'outlet';
  position: number;
  isMandatory: boolean;
  failurePolicy: 'terminal' | 'warn';
  isActive: boolean;
}

export interface AddChainEntryInput {
  wrapperFunctionId: string;
  chainKind: 'inlet' | 'outlet';
  position?: number;
  isMandatory?: boolean;
  failurePolicy?: 'terminal' | 'warn';
}

export interface UpdateChainEntryInput {
  position?: number;
  isMandatory?: boolean;
  failurePolicy?: 'terminal' | 'warn';
  isActive?: boolean;
}

export function useAddModelChainEntry(brandId: string) {
  const qc = useQueryClient();
  return useMutation<ChainEntry, Error, AddChainEntryInput>({
    mutationFn: (input) => apiClient.post(`/admin/platform/argus-models/${brandId}/chains`, input),
    onSuccess: () => { qc.invalidateQueries({ queryKey: KEYS.detail(brandId) }); },
  });
}

export function useUpdateModelChainEntry(brandId: string) {
  const qc = useQueryClient();
  return useMutation<ChainEntry, Error, { chainId: string; body: UpdateChainEntryInput }>({
    mutationFn: ({ chainId, body }) => apiClient.patch(`/admin/platform/argus-models/${brandId}/chains/${chainId}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: KEYS.detail(brandId) }); },
  });
}

export function useDeleteModelChainEntry(brandId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (chainId) => apiClient.delete(`/admin/platform/argus-models/${brandId}/chains/${chainId}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: KEYS.detail(brandId) }); },
  });
}

// ─── Engine dispatch config (config-as-data) ─────────────────────────────
// Edit how a brand dispatches its base LLM: context window, sampling, system
// prompt, guardrail profile, eligibility, role. PATCH /argus-models/:id/engines/:engineId.

export interface UpdateEngineInput {
  roleLabel?: string;
  isDefault?: boolean;
  provenanceEligibility?: string[];
  defaultTemperature?: number;
  defaultTopP?: number;
  defaultTopK?: number | null;
  defaultMaxTokens?: number | null;
  defaultRepeatPenalty?: number | null;
  defaultCtxSize?: number;
  systemPrompt?: string;
  guardrailProfile?: 'baseline' | 'standard' | 'maximum';
}

export function useUpdateModelEngine(brandId: string) {
  const qc = useQueryClient();
  return useMutation<{ id: string }, Error, { engineId: string; body: UpdateEngineInput }>({
    mutationFn: ({ engineId, body }) => apiClient.patch(`/admin/platform/argus-models/${brandId}/engines/${engineId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.detail(brandId) });
      qc.invalidateQueries({ queryKey: KEYS.catalog });
    },
  });
}

export interface CreateTrustToggleInput {
  configKey: string;
  label: string;
  description: string;
  defaultState: boolean;
  wrapperFunctionId: string;
  appliesToBrands?: string[] | null;
  sortOrder?: number;
}

export function useCreateTrustToggle() {
  const qc = useQueryClient();
  return useMutation<{ id: string }, Error, CreateTrustToggleInput>({
    mutationFn: (input) => apiClient.post('/admin/platform/argus-trust-toggles', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.trustToggles });
    },
  });
}

// ─── M6c Phase D mutations ─────────────────────────────────────────────────

export interface UpdateWrapperFunctionInput {
  id: string;
  displayName?: string;
  description?: string;
  category?: string;
  appliesTo?: 'inlet' | 'outlet' | 'both';
  latencyBudgetMs?: number;
  version?: string;
  isActive?: boolean;
}

/** Toggle a wrapper function's active flag (sysadmin retirement workflow). */
export function useUpdateWrapperFunction() {
  const qc = useQueryClient();
  return useMutation<{ id: string }, Error, UpdateWrapperFunctionInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient.patch(`/admin/platform/argus-wrapper-functions/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.wrapperFunctions });
    },
  });
}

// ─── Wave 3 PR-B — wrapper function authoring ──────────────────────────────

export interface CreateWrapperFunctionInput {
  functionKey: string;
  displayName: string;
  description: string;
  runtime: 'ts' | 'py';
  source: string;
  category: string;
  appliesTo: 'inlet' | 'outlet' | 'both';
  latencyBudgetMs?: number;
  version?: string;
}

// ─── Sysadmin GENERAL system-function authoring ────────────────────────────
// The function builder at sysadmin scope, producing a SYSTEM general callable
// function (org_id NULL) that every tenant can see + call (source hidden) —
// distinct from the inlet/outlet filter wrapper functions above. Reuses the
// SAME shared persist + system-tool mirror as the backend (one pipeline).
export interface CreateSystemFunctionInput {
  name: string;
  description: string;
  runtime: 'typescript' | 'python' | 'ts' | 'py';
  source: string;
  inputSchemaJson?: string;
  outputSchemaJson?: string;
  sideEffects?: string[];
  networkHosts?: string[];
  sandboxTestPassed?: boolean;
  sandboxTestSampleCount?: number;
}

export function useCreateSystemFunction() {
  const qc = useQueryClient();
  return useMutation<{ data: { id: string; slug: string } }, Error, CreateSystemFunctionInput>({
    mutationFn: (input) =>
      apiClient.post('/admin/platform/catalog/system-functions', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.wrapperFunctions }),
  });
}

export function useCreateWrapperFunction() {
  const qc = useQueryClient();
  return useMutation<{ id: string }, Error, CreateWrapperFunctionInput>({
    mutationFn: (input) =>
      apiClient.post('/admin/platform/argus-wrapper-functions', input),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: KEYS.wrapperFunctions }),
  });
}

export function useUpdateWrapperFunctionSource() {
  const qc = useQueryClient();
  return useMutation<
    { id: string; isActive: boolean; signature: string | null },
    Error,
    { id: string; source: string }
  >({
    mutationFn: ({ id, source }) =>
      apiClient.request(`/admin/platform/argus-wrapper-functions/${id}/source`, {
        method: 'PUT',
        body: JSON.stringify({ source }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: KEYS.wrapperFunctions }),
  });
}

export function useSignWrapperFunction() {
  const qc = useQueryClient();
  return useMutation<
    { id: string; signingKeyId: string; sourceHash: string },
    Error,
    { id: string }
  >({
    mutationFn: ({ id }) =>
      apiClient.post(`/admin/platform/argus-wrapper-functions/${id}/sign`, {}),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: KEYS.wrapperFunctions }),
  });
}

export function useDeleteWrapperFunction() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.delete(`/admin/platform/argus-wrapper-functions/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: KEYS.wrapperFunctions }),
  });
}

// ─── PR-G — wrapper function publish lifecycle ─────────────────────────────

export interface PublishWrapperFunctionResult {
  id: string;
  status: 'published';
  publishedAt: string;
  publishedBy: string | null;
}

/**
 * Publish a wrapper function. Requires the function to already be:
 *   - is_active=true (signed + activated)
 *   - non-native rows: a non-null signature
 *
 * Idempotent on already-published rows.
 */
export function usePublishWrapperFunction() {
  const qc = useQueryClient();
  return useMutation<PublishWrapperFunctionResult, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.post(`/admin/platform/argus-wrapper-functions/${id}/publish`, {}),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: KEYS.wrapperFunctions }),
  });
}

export interface UnpublishWrapperFunctionInput {
  id: string;
  reason: string;
  nextStatus: 'deprecated' | 'retired';
}

export interface UnpublishWrapperFunctionResult {
  id: string;
  status: 'deprecated' | 'retired';
  unpublishedAt: string;
  unpublishedBy: string | null;
  unpublishReason: string;
  isActive: boolean;
}

/**
 * Unpublish a wrapper function. nextStatus picks the terminal state:
 *   - 'deprecated': existing tenants keep working, no new tenants get
 *                    visibility, is_active stays where it was.
 *   - 'retired':    function is taken offline (is_active forced false).
 *
 * reason must be at least 10 characters — surfaces in the audit trail.
 */
export function useUnpublishWrapperFunction() {
  const qc = useQueryClient();
  return useMutation<
    UnpublishWrapperFunctionResult,
    Error,
    UnpublishWrapperFunctionInput
  >({
    mutationFn: ({ id, reason, nextStatus }) =>
      apiClient.post(
        `/admin/platform/argus-wrapper-functions/${id}/unpublish`,
        { reason, nextStatus },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: KEYS.wrapperFunctions }),
  });
}

export interface WrapperFunctionUsage {
  argusModelId: string;
  brandId: string;
  displayName: string;
  modality: string;
  chainKind: 'inlet' | 'outlet';
  position: number;
  isMandatory: boolean;
  failurePolicy: 'terminal' | 'warn';
  isActive: boolean;
}

/**
 * Returns the brands that reference a wrapper function in their chain
 * + how many Trust toggles wire to it. Used by the function library
 * detail dialog so sysadmin sees the blast radius before retiring.
 */
export function useWrapperFunctionUsage(id: string | null | undefined) {
  return useQuery<{ items: WrapperFunctionUsage[]; trustToggleCount: number }, Error>({
    queryKey: id ? ['admin', 'argus-wrapper-functions', id, 'usage'] : ['admin', 'argus-wrapper-functions', '__none__', 'usage'],
    queryFn: () => apiClient.get(`/admin/platform/argus-wrapper-functions/${id}/usage`),
    enabled: !!id,
  });
}

export interface UpdateTrustToggleInput {
  id: string;
  label?: string;
  description?: string;
  defaultState?: boolean;
  appliesToBrands?: string[] | null;
  sortOrder?: number;
  isActive?: boolean;
}

/**
 * Edit a Trust toggle's tenant-visible label/description, default
 * state, applied-brands list, sort order, or retire it.
 *
 * config_key + wrapper_function_id are immutable post-create —
 * changing either silently re-meanings the toggle for tenants who
 * already opted in. Retire + recreate is the supported path.
 */
export function useUpdateTrustToggle() {
  const qc = useQueryClient();
  return useMutation<{ id: string }, Error, UpdateTrustToggleInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient.patch(`/admin/platform/argus-trust-toggles/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.trustToggles });
    },
  });
}

export interface UpdateAssignmentInput {
  id: string;
  argusModelEngineId?: string;
  isEnabled?: boolean;
  sysadminNotes?: string | null;
}

export function useUpdateAssignment() {
  const qc = useQueryClient();
  return useMutation<{ id: string }, Error, UpdateAssignmentInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient.patch(`/admin/platform/tenant-argus-model-assignments/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'tenant-argus-model-assignments'] });
      qc.invalidateQueries({ queryKey: ['platform-tenant-argus-assignments'] });
      qc.invalidateQueries({ queryKey: KEYS.catalog });
    },
  });
}

// Wave 3 §10.2 #3a — POST + DELETE assignment mutations driving the
// Tenant Detail Assignments tab.

export interface CreateAssignmentInput {
  organizationId: string;
  argusModelId: string;
  argusModelEngineId: string;
  isEnabled?: boolean;
  sysadminNotes?: string | null;
}

export interface CreateAssignmentResult {
  id: string;
  alreadyExists?: boolean;
}

export function useCreateAssignment() {
  const qc = useQueryClient();
  return useMutation<CreateAssignmentResult, Error, CreateAssignmentInput>({
    mutationFn: (input) =>
      apiClient.post('/admin/platform/tenant-argus-model-assignments', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'tenant-argus-model-assignments'] });
      qc.invalidateQueries({ queryKey: ['platform-tenant-argus-assignments'] });
      qc.invalidateQueries({ queryKey: KEYS.catalog });
    },
  });
}

export function useDeleteAssignment() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.delete(`/admin/platform/tenant-argus-model-assignments/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'tenant-argus-model-assignments'] });
      qc.invalidateQueries({ queryKey: ['platform-tenant-argus-assignments'] });
      qc.invalidateQueries({ queryKey: KEYS.catalog });
    },
  });
}

/** Manual load (sysadmin debug). Returns 202 — the actual load is async. */
export function useLoadEngine() {
  return useMutation<{ status: string }, Error, { argusModelId: string; engineId: string }>({
    mutationFn: ({ argusModelId, engineId }) =>
      apiClient.post(
        `/admin/platform/argus-models/${argusModelId}/engines/${engineId}/load`,
        {},
      ),
  });
}

export function useUnloadEngine() {
  return useMutation<{ status: string }, Error, { argusModelId: string; engineId: string }>({
    mutationFn: ({ argusModelId, engineId }) =>
      apiClient.post(
        `/admin/platform/argus-models/${argusModelId}/engines/${engineId}/unload`,
        {},
      ),
  });
}

// ─── Runtime / hardware monitor (Phase E) ────────────────────────────────────

export interface RuntimeHardwareSnapshot {
  host: {
    available: boolean;
    hostname: string;
    os: string;
    kernel: string;
    cpuCores: number;
    cpuModel: string | null;
    memoryTotalMb: number | null;
    memoryUsedMb: number | null;
    memoryFreeMb: number | null;
    diskTotalGb: number | null;
    diskUsedGb: number | null;
    diskFreeGb: number | null;
    loadAvg: [number, number, number];
    uptimeSeconds: number;
    notes: string[];
  };
  gpu: {
    available: boolean;
    unavailableReason: string | null;
    gpus: Array<{
      index: number;
      name: string;
      vramTotalMb: number | null;
      vramUsedMb: number | null;
      utilizationPercent: number | null;
      temperatureC: number | null;
    }>;
  };
  gateway: {
    healthy: boolean;
    brandCount: number | null;
    brands: string[];
    probeError: string | null;
  };
  engines: {
    total: number;
    registered: Array<{
      argusModelId: string;
      brandId: string;
      engineId: string;
      backendModelName: string;
      baseLlmKey: string;
      isDefault: boolean;
      vramGb: number | null;
      visibleAtGateway: boolean;
    }>;
  };
  collectedAt: string;
}

/**
 * Hardware + runtime snapshot. Refetches every 10 s when the runtime
 * page is visible. Aggregates host / GPU / gateway / loaded-engine
 * facts into a single sysadmin operational view.
 */
export function useRuntimeHardware() {
  return useQuery<RuntimeHardwareSnapshot>({
    queryKey: ['admin', 'argus-runtime', 'hardware'],
    queryFn: () => apiClient.get('/admin/platform/argus-runtime/hardware'),
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
}
